import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import type { UserProfile } from '../types';

interface ProfileCompletionStatus {
  readonly completionPercentage: number;
  readonly missingFields: string[];
  readonly nextPrompt: string | null;
}

interface SessionInitResponse {
  readonly profile: UserProfile;
  readonly profileCompletion: ProfileCompletionStatus;
  readonly readStatus: { readonly readArticleIds: string[]; readonly seenArticleIds: string[] };
  readonly unreadNotificationCount: number;
  readonly savedArticleHashes: string[];
  readonly subscribedChannelIds?: string[];
}

// Mirrors mobile/src/hooks/use-session-init-query.ts. Seeds the individual
// query caches inside the queryFn (NOT in a useEffect) so gated observers
// like useProfileQuery and useSavedQuery read populated data the moment
// their `enabled` flag flips true.
function seedCachesFromInit(qc: QueryClient, data: SessionInitResponse): void {
  qc.setQueryData(queryKeys.profile, data.profile);
  qc.setQueryData(queryKeys.profileCompletion, data.profileCompletion);
  qc.setQueryData(queryKeys.feed.readStatus, data.readStatus);
  qc.setQueryData(queryKeys.notifications.unreadCount(), data.unreadNotificationCount);
  qc.setQueryData(queryKeys.saved.list, data.savedArticleHashes);
}

export function useSessionInitQuery(options?: { readonly enabled?: boolean }) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.sessionInit,
    queryFn: async (): Promise<SessionInitResponse> => {
      const result = await apiClient.get<{ data: SessionInitResponse }>('/session/init');
      const data = result.data.data;
      seedCachesFromInit(queryClient, data);
      return data;
    },
    staleTime: Infinity,
    retry: (failureCount, error) => {
      const status = (error as { statusCode?: number }).statusCode;
      if (status === 401 || status === 404) return false;
      return failureCount < 1;
    },
    enabled: options?.enabled ?? true,
  });
}
